import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { NOTIFICATION_FUNCTION_MAP } from 'web-library/models/AuthorityFunctions';
import { useGetNotificationTemplateDetails } from '../../../api-refactored/manage-notifications/useGetNotificationTemplateDetails';
import { useGetNotificationDetails } from '../../../api-refactored/manage-notifications/useGetNotificationDetails';
import { useGetNotificationFeature } from '../../../api-refactored/manage-notifications/useGetNotificationFeature';
import { useGetAnnouncementCandidateTypes } from '../../../api-refactored/manage-announcements/useGetAnnouncementCandidateTypes';
import { useGetAnnouncementExamLevels } from '../../../api-refactored/manage-announcements/useGetAnnouncementExamLevels';
import { useGetAnnouncementExamCentres } from '../../../api-refactored/manage-announcements/useGetAnnouncementExamCentres';
import { RecipientTypeMode, SelectRecipientByMode } from './index';
import { stringToDefaultDisplay } from '../../../utils/DateUtils';
import { usePostCreateNotification } from '../../../api-refactored/manage-notifications/usePostCreateNotification';
import RouteKey from '../../../routes/key';
import { NotificationSearchRecipientResponseContentObject } from '../../../api-refactored/manage-notifications/usePostNotifcationSearchRecipient';
import {
  NotificationRecipientSelectRequesteContentObject,
  usePutEditNotification,
} from '../../../api-refactored/manage-notifications/usePutEditNotification';

const { admin } = RouteKey.child;
const { manageNotifications } = RouteKey.admin.child;

const candidateTypesPayload = (
  recipientTypeMode: RecipientTypeMode,
  candidateTypeValue: string[]
) => (recipientTypeMode === RecipientTypeMode.Candidate ? candidateTypeValue : null);

const examLevelsPayload = (recipientTypeMode: RecipientTypeMode, examLevelValue: string[]) =>
  recipientTypeMode === RecipientTypeMode.Candidate ? examLevelValue : null;

interface useSendNotificationProps {
  featureValue: string;
  examLevelValue: string[];
  candidateTypeValue: string[];
  schoolValue: string[];
  isEdit: boolean;
  acknowledgementCheckbox: boolean;
  sendDate: string;
  recipientTypeMode: RecipientTypeMode;
  selectedCandidates: NotificationSearchRecipientResponseContentObject[];
  selectRecipientByMode: SelectRecipientByMode;
  setAcknowledgementCheckbox: React.Dispatch<React.SetStateAction<boolean>>;
  setExamLevelValue: React.Dispatch<React.SetStateAction<string[]>>;
  setCandidateTypeValue: React.Dispatch<React.SetStateAction<string[]>>;
  setSchoolValue: React.Dispatch<React.SetStateAction<string[]>>;
  setSendDate: React.Dispatch<React.SetStateAction<string>>;
  setFeatureValue: React.Dispatch<React.SetStateAction<string>>;
  setRecipientTypeMode: React.Dispatch<React.SetStateAction<RecipientTypeMode>>;
  setSelectRecipientByMode: React.Dispatch<React.SetStateAction<SelectRecipientByMode>>;
  setSelectedCandidates: React.Dispatch<
    React.SetStateAction<NotificationSearchRecipientResponseContentObject[]>
  >;
  sendNotificationError: string | undefined;
  setSendNotificationError: React.Dispatch<React.SetStateAction<string | undefined>>;
}

export const useSendNotification = ({
  featureValue,
  examLevelValue,
  candidateTypeValue,
  schoolValue,
  isEdit,
  acknowledgementCheckbox,
  sendDate,
  recipientTypeMode,
  selectRecipientByMode,
  selectedCandidates,
  setAcknowledgementCheckbox,
  setExamLevelValue,
  setCandidateTypeValue,
  setSchoolValue,
  setSendDate,
  setFeatureValue,
  setSelectRecipientByMode,
  setRecipientTypeMode,
  setSelectedCandidates,
  setSendNotificationError
}: useSendNotificationProps) => {
  const { uuid } = useParams<{ uuid: string }>();
  const history = useHistory();

  const {
    data: notificationTemplateDetails,
    isLoading: isLoadingNotificationTemplateDetails,
  } = useGetNotificationTemplateDetails({
    uuid,
    enabled: !isEdit,
  });

  const {
    data: notificationDetails,
    isLoading: isLoadingNotificationDetails,
  } = useGetNotificationDetails({
    uuid,
    enabled: isEdit,
    onSuccess: (res) => {
      setAcknowledgementCheckbox(res.ackRequired);
      setRecipientTypeMode(
        res.recipientSelection.candidateRecipient
          ? RecipientTypeMode.Candidate
          : RecipientTypeMode.Coordinator
      );
      setSelectRecipientByMode(
        res.recipientSelection.individualRecipient
          ? SelectRecipientByMode.Individual
          : SelectRecipientByMode.UserGroup
      );
      setExamLevelValue(res.recipientSelection.examLevels);
      setCandidateTypeValue(res.recipientSelection.candidateTypes);
      setSchoolValue(res.recipientSelection.examCentres);
      setSelectedCandidates(
        res.recipientSelection.recipients
          ? res.recipientSelection.recipients.map((recipient) => ({
              uuid: recipient.userUuid,
              nricFinPassport: recipient.nricFinPassport,
              name: recipient.name,
              indexNo: recipient.indexNo || '',
              examCentre: recipient.examCentre || '',
              emailMobile: recipient.emailMobile,
            }))
          : []
      );
      setSendDate(res.sendDate);
    },
  });

  const { data: notificationFeature } = useGetNotificationFeature({
    onSuccess: (res) => {
      if (!featureValue) {
        setFeatureValue(res[0].code);
      }
    },
  });

  const {
    data: notificationCandidateTypes,
    isLoading: isLoadingNotificationCandidateTypes,
  } = useGetAnnouncementCandidateTypes({
    onSuccess: (res) => {
      if (candidateTypeValue?.length === 0) {
        setCandidateTypeValue([`${res[0].code}`]);
      }
    },
  });
  const {
    data: notificationExamLevels,
    isLoading: isLoadingNotificationExamLevels,
  } = useGetAnnouncementExamLevels({
    functionName: NOTIFICATION_FUNCTION_MAP[featureValue],
    onSuccess: (res) => {
      if (!examLevelValue?.length && res.length) {
        setExamLevelValue([`${res[0].code}`]);
      }
    },
  });

  const {
    data: notificationSchools,
    isLoading: isLoadingNotificationSchools,
  } = useGetAnnouncementExamCentres({
    onSuccess: (res) => {
      if (!schoolValue?.length) {
        setSchoolValue([`${res[0].code}`]);
      }
    },
    enabled: true,
  });

  const handleBackToNotificationListing = () =>
    history.push(`/${admin}/${manageNotifications.route}`, {
      featureValue,
    });

  const {
    mutate: mutatePostCreateNotification,
    status: statusCreateNotification,
  } = usePostCreateNotification({
    onSuccess: () => handleBackToNotificationListing(),
    onError: (err) => setSendNotificationError(err?.response?.data.message)
  });

  const {
    mutate: mutatePutEditNotification,
    status: statusEditNotification,
  } = usePutEditNotification({
    onSuccess: () => handleBackToNotificationListing(),
    onError: (err) => setSendNotificationError(err?.response?.data.message),
    uuid,
  });

  const handleCreateNotification = () => {
    const selectedCandidatesWithKeyChanged: NotificationRecipientSelectRequesteContentObject[] = selectedCandidates.map(
      ({ uuid: selectedCandidateUuid, ...selectedCandidate }) => ({
        userUuid: selectedCandidateUuid,
        ...selectedCandidate,
      })
    );

    switch (recipientTypeMode) {
      case RecipientTypeMode.Candidate:
        if (
          selectRecipientByMode === SelectRecipientByMode.UserGroup &&
          (candidateTypeValue.length === 0 ||
            examLevelValue.length === 0 ||
            schoolValue.length === 0)
        ) {
          return;
        }
        break;
      case RecipientTypeMode.Coordinator:
        if (schoolValue.length === 0) {
          return;
        }
        break;
      default:
        break;
    }

    if (!isEdit) {
      mutatePostCreateNotification({
        notificationType: featureValue,
        title: isEdit ? notificationDetails?.title : notificationTemplateDetails?.title,
        message: isEdit ? notificationDetails?.message : notificationTemplateDetails?.message,
        ackRequired: acknowledgementCheckbox,
        recipientSelection: {
          candidateRecipient: recipientTypeMode === RecipientTypeMode.Candidate,
          individualRecipient: selectRecipientByMode === SelectRecipientByMode.Individual,
          candidateTypes: candidateTypesPayload(recipientTypeMode, candidateTypeValue),
          examLevels: examLevelsPayload(recipientTypeMode, examLevelValue),
          examCentres: schoolValue,
          recipients: selectedCandidatesWithKeyChanged,
        },
        sendDate: stringToDefaultDisplay(sendDate),
      });
    } else {
      mutatePutEditNotification({
        notificationType: featureValue,
        title:
          isEdit && notificationDetails
            ? notificationDetails.title
            : notificationTemplateDetails.title,
        message:
          isEdit && notificationDetails
            ? notificationDetails.message
            : notificationTemplateDetails.message,
        ackRequired: acknowledgementCheckbox,
        recipientSelection: {
          candidateRecipient: recipientTypeMode === RecipientTypeMode.Candidate,
          individualRecipient: selectRecipientByMode === SelectRecipientByMode.Individual,
          candidateTypes: candidateTypesPayload(recipientTypeMode, candidateTypeValue),
          examLevels: examLevelsPayload(recipientTypeMode, examLevelValue),
          examCentres: schoolValue,
          recipients: selectedCandidatesWithKeyChanged,
        },
        sendDate: stringToDefaultDisplay(sendDate),
      });
    }
  };

  const isLoadingProgressIndicator =
    (isEdit && isLoadingNotificationDetails) ||
    (!isEdit && isLoadingNotificationTemplateDetails) ||
    isLoadingNotificationCandidateTypes ||
    isLoadingNotificationExamLevels ||
    isLoadingNotificationSchools;

  return {
    notificationTemplateDetails,
    notificationDetails,
    notificationFeature,
    notificationCandidateTypes,
    notificationExamLevels,
    notificationSchools,
    statusCreateNotification,
    statusEditNotification,
    handleCreateNotification,
    isLoadingProgressIndicator,
  };
};
